import { useRef, useState, useCallback, useEffect } from 'react';
import config from '../../../config';
import { getLocalAccessToken } from 'modules/user/userCookies';
import {
  getAccessTokenRefreshDelay,
  performRefresh,
} from 'modules/user/silentRefresh';
import { useQueryClient } from '@tanstack/react-query';

export interface Subscription {
  channel: string;
  data: any;
  auth?: boolean;
}

interface WebSocketServerReturn {
  subscribe: (subscription: Subscription) => void;
  unsubscribe: (subscription: Subscription) => void;
  addGlobalMessageListener: (callback: (msg: any) => void) => void;
  removeGlobalMessageListener: (callback: (msg: any) => void) => void;
  isConnected: boolean | undefined;
  isConnecting: boolean;
  getSocket?: () => WebSocket | null;
  connect: () => void;
  disconnect: () => void;
  waitForSocketOpen: () => Promise<void>;
}

const WEBSOCKET_URL = config.WEBSOCKET_URL;

const getSessionToken = () => {
  const existingToken = localStorage.getItem('websocketSessionToken');
  if (existingToken) return existingToken;

  const randomToken = crypto.randomUUID();
  localStorage.setItem('websocketSessionToken', randomToken);
  return randomToken;
};

export const useWebSocketServer = (): WebSocketServerReturn => {
  const [isConnected, setIsConnected] = useState<boolean | undefined>(
    undefined
  );
  const [isConnecting, setIsConnecting] = useState(false);
  const queryClient = useQueryClient();

  const socketRef = useRef<WebSocket | null>(null);

  const disconnectedDueToSleep = useRef(false); // 🔧 new flag

  const globalListeners = useRef<((msg: any) => void)[]>([]);

  const sendMessage = (message: any) => {
    if (socketRef.current?.readyState === WebSocket.OPEN) {
      socketRef.current.send(JSON.stringify(message));
    } else {
      console.warn('⚠️ WebSocket not connected. Message not sent:', message);
    }
  };

  const connectWebSocket = useCallback(() => {
    if (
      socketRef.current &&
      socketRef.current.readyState !== WebSocket.CLOSED
    ) {
      socketRef.current.close();
    }

    const socket = new WebSocket(`${WEBSOCKET_URL}?token=${getSessionToken()}`);
    socketRef.current = socket;

    setIsConnecting(true);

    socket.onopen = () => {
      setIsConnected(true);
      setIsConnecting(false);
    };

    socket.onclose = (event) => {
      setIsConnected(false);
      setIsConnecting(false);
      if (event.code === 1006) {
        console.warn('WebSocket closed unexpectedly — possibly system sleep?');
        disconnectedDueToSleep.current = true; // 🔧 mark as due to sleep
      }
    };

    socket.onmessage = (event) => {
      try {
        const message = JSON.parse(event.data);
        const isGlobalMessage = !message.channel && message.type;
        if (isGlobalMessage) {
          globalListeners.current.forEach((fn) => fn(message));
        }
      } catch (err) {
        console.error('❌ Failed to parse WebSocket message:', err);
      }
    };

    socket.onerror = (error) => {
      console.error('❌ WebSocket Error:', error);
      if (socket.readyState !== WebSocket.CLOSED) {
        socket.close();
      }
    };
  }, []);

  const subscribe = (subscription: Subscription) => {
    sendMessage({
      action: 'subscribe',
      channel: subscription.channel,
      data: subscription.data,
    });
  };

  const unsubscribe = (subscription: Subscription) => {
    sendMessage({
      action: 'unsubscribe',
      channel: subscription.channel,
      data: subscription.data,
    });
  };

  const waitForSocketOpen = (): Promise<void> => {
    return new Promise((resolve) => {
      const socket = socketRef.current;
      if (socket?.readyState === WebSocket.OPEN) {
        return resolve();
      }
      const interval = setInterval(() => {
        if (socketRef.current?.readyState === WebSocket.OPEN) {
          clearInterval(interval);
          resolve();
        }
      }, 50);
    });
  };

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (
        document.visibilityState === 'visible' &&
        disconnectedDueToSleep.current
      ) {
        // 👇 Async IIFE inside sync function
        (async () => {
          disconnectedDueToSleep.current = false;
          const token = getLocalAccessToken();
          const shouldRefresh =
            !token || getAccessTokenRefreshDelay(token, 5) <= 0;

          if (shouldRefresh) {
            await performRefresh({ isNotSilentRefresh: true }); // refresh token logic
          }

          connectWebSocket(); // initiate WebSocket connection
          await waitForSocketOpen(); // wait until it's open

          const latestToken = getLocalAccessToken();
          const socket = socketRef.current;

          if (latestToken && socket instanceof WebSocket) {
            socket.send(
              JSON.stringify({
                action: 'authenticate',
                channel: 'auth',
                data: { token: latestToken },
              })
            );
          } else {
            console.warn('❌ No token available for WebSocket auth');
          }

          await queryClient.invalidateQueries();
        })().catch((err) =>
          console.error('Error during visibility reconnect:', err)
        );
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [connectWebSocket, waitForSocketOpen]);

  const addGlobalMessageListener = (callback: (msg: any) => void) => {
    globalListeners.current.push(callback);
  };

  const removeGlobalMessageListener = (callback: (msg: any) => void) => {
    globalListeners.current = globalListeners.current.filter(
      (fn) => fn !== callback
    );
  };

  const disconnect = () => {
    if (socketRef.current) {
      console.log('🔌 Disconnecting WebSocket manually...');
      socketRef.current.onopen = null;
      socketRef.current.onclose = null;
      socketRef.current.onerror = null;
      socketRef.current.onmessage = null;
      socketRef.current.close();
      socketRef.current = null;
    }
    setIsConnected(false);
    setIsConnecting(false);
  };

  return {
    subscribe,
    unsubscribe,
    isConnected,
    isConnecting,
    getSocket: () => socketRef.current,
    addGlobalMessageListener,
    removeGlobalMessageListener,
    connect: connectWebSocket,
    disconnect,
    waitForSocketOpen,
  };
};
